import { apiService } from './apiService'
import { ApiResponse } from '../types/partner'
import {
  RecurringSchedule,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  RecurringTaskExecution,
  ApprovalItem,
  BlitzReview,
  BlitzDecision,
  BlitzAutoSyncRule,
} from '../types/recurringTasks'

class RecurringTaskService {
  // ─── Schedules ──────────────────────────────────────────────────

  async getSchedules(partnerId: string, customerId: string): Promise<ApiResponse<RecurringSchedule[]>> {
    return apiService.get<RecurringSchedule[]>(`/partner/${partnerId}/customer/${customerId}/recurring-schedules`)
  }

  async createSchedule(partnerId: string, customerId: string, data: CreateScheduleRequest): Promise<ApiResponse<RecurringSchedule>> {
    return apiService.post<RecurringSchedule>(`/partner/${partnerId}/customer/${customerId}/recurring-schedules`, data)
  }

  async updateSchedule(partnerId: string, customerId: string, scheduleId: string, data: UpdateScheduleRequest): Promise<ApiResponse<RecurringSchedule>> {
    return apiService.put<RecurringSchedule>(`/partner/${partnerId}/customer/${customerId}/recurring-schedules/${scheduleId}`, data)
  }

  async deleteSchedule(partnerId: string, customerId: string, scheduleId: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`/partner/${partnerId}/customer/${customerId}/recurring-schedules/${scheduleId}`)
  }

  async triggerSchedule(partnerId: string, customerId: string, scheduleId: string): Promise<ApiResponse<RecurringTaskExecution>> {
    return apiService.post<RecurringTaskExecution>(`/partner/${partnerId}/customer/${customerId}/recurring-schedules/${scheduleId}/trigger`)
  }

  // ─── Executions ─────────────────────────────────────────────────

  async getExecutions(partnerId: string, customerId: string): Promise<ApiResponse<RecurringTaskExecution[]>> {
    return apiService.get<RecurringTaskExecution[]>(`/partner/${partnerId}/customer/${customerId}/recurring-executions`)
  }

  // ─── Approvals ──────────────────────────────────────────────────

  async reviewBlitz(partnerId: string, approvalId: string): Promise<ApiResponse<BlitzReview>> {
    return apiService.get<BlitzReview>(`/partner/${partnerId}/approvals/${approvalId}/blitz-review`)
  }

  async approveBlitz(partnerId: string, approvalId: string, decision: BlitzDecision): Promise<ApiResponse<BlitzReview>> {
    return apiService.post<BlitzReview>(`/partner/${partnerId}/approvals/${approvalId}/blitz-approve`, decision)
  }

  async setBlitzAutoSync(partnerId: string, customerId: string, scheduleId: string, enabled: boolean): Promise<ApiResponse<BlitzAutoSyncRule>> {
    return apiService.put<BlitzAutoSyncRule>(`/partner/${partnerId}/customer/${customerId}/recurring-schedules/${scheduleId}/blitz-auto-sync`, { enabled })
  }

  async getApprovals(partnerId: string, params?: { status?: string; customerId?: string }): Promise<ApiResponse<ApprovalItem[]>> {
    return apiService.get<ApprovalItem[]>(`/partner/${partnerId}/approvals`, params)
  }

  async getApproval(partnerId: string, approvalId: string): Promise<ApiResponse<ApprovalItem>> {
    return apiService.get<ApprovalItem>(`/partner/${partnerId}/approvals/${approvalId}`)
  }

  async approveItem(partnerId: string, approvalId: string): Promise<ApiResponse<ApprovalItem>> {
    return apiService.post<ApprovalItem>(`/partner/${partnerId}/approvals/${approvalId}/approve`)
  }

  async rejectItem(partnerId: string, approvalId: string, reason: string): Promise<ApiResponse<Pick<ApprovalItem, 'id' | 'status' | 'rejectedReason' | 'updatedAt'>>> {
    return apiService.post<Pick<ApprovalItem, 'id' | 'status' | 'rejectedReason' | 'updatedAt'>>(`/partner/${partnerId}/approvals/${approvalId}/reject`, { reason })
  }

  async getAttachmentDownloadUrl(partnerId: string, approvalId: string, attachmentId: string): Promise<ApiResponse<{ url: string; fileName: string }>> {
    return apiService.get<{ url: string; fileName: string }>(`/partner/${partnerId}/approvals/${approvalId}/attachments/${attachmentId}/download`)
  }

  /** Stream attachment content through the API (avoids CORS on blob storage). */
  async getAttachmentContent(partnerId: string, approvalId: string, attachmentId: string): Promise<Blob> {
    return apiService.getBlob(`/partner/${partnerId}/approvals/${approvalId}/attachments/${attachmentId}/content`)
  }
}

export const recurringTaskService = new RecurringTaskService()
